import { ChangeDetectionStrategy, Component, OnInit, inject, output, signal } from '@angular/core';
import { IonText, LoadingController } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICoords } from '@okcargo-apps/shared-domain';
import { GeolocationService } from '@okcargo-apps/shared-infrastructure-ionic';

@Component({
  selector: 'lib-freight-carrier-stop-geolocation',
  templateUrl: './freight-carrier-stop-geolocation.component.html',
  styleUrls: ['./freight-carrier-stop-geolocation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonText, TranslateModule],
})
export class FreightCarrierStopGeolocationComponent implements OnInit {
  private readonly geolocationService = inject(GeolocationService);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);

  readonly coordsChange = output<ICoords | null>();

  public coords = signal<ICoords | null>(null);
  public geolocationErrors = signal<string | null>(null);

  ngOnInit(): void {
    this.getGeolocation();
  }

  public retryGeolocation(): void {
    this.getGeolocation();
  }

  private async getGeolocation(): Promise<void> {
    this.geolocationErrors.set(null);

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('freight.general.get_geolocation'),
    });
    await loading.present();

    try {
      const position = await this.geolocationService.getCurrentPosition();
      this.coords.set(position);
      if (!position) {
        console.error('No se pudo obtener la geolocalización');
        this.geolocationErrors.set('error.geolocation_failed');
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'permissions_not_granted') {
        this.geolocationErrors.set('error.permissions_not_granted');
      } else {
        console.error('Error al obtener la geolocalización');
        this.handleGeolocationError(error as GeolocationPositionError);
      }
    } finally {
      this.coordsChange.emit(this.coords());
      await loading.dismiss();
    }
  }

  private handleGeolocationError(error: GeolocationPositionError | Error): void {
    if ('code' in error) {
      switch (error.code) {
        case 1:
          this.geolocationErrors.set('error.permissions_not_granted');
          break;
        case 2:
          this.geolocationErrors.set('error.position_unavailable');
          break;
        case 3:
          this.geolocationErrors.set('error.geolocation_timeout');
          break;
        default:
          this.geolocationErrors.set('error.geolocation_failed');
          break;
      }
    } else {
      this.geolocationErrors.set('error.geolocation_failed');
    }
  }
}
